import { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppLayout } from '@/components/layout/AppLayout';
import { PageHeader } from '@/components/layout/PageHeader';
import { ChatHistoryPanel } from '@/components/chat/ChatHistoryPanel';
import { useAuth } from '@/utils/AuthContext';
import { personasApi, projectsApi, researchChatsApi, aiApi, chatMessagesApi } from '@/utils/api-pb';
import type { Persona, Project, ResearchChat } from '@/utils/pocketbase';
import { extractPersonaName } from '@/utils/markdown';
import { toast } from 'sonner';
import { SubscriptionLimitBanner } from '@/components/shared/SubscriptionLimitBanner';

import { useTranslation } from 'react-i18next';
import { useLanguage } from '@/i18n/LanguageContext';
// Функция для обработки жирного текста в строке
function processBoldText(text: string, keyPrefix: string): (string | JSX.Element)[] {
  const parts: (string | JSX.Element)[] = [];
  let partIndex = 0;

  const boldRegex = /(\*\*(.+?)\*\*)|(\*(.+?)\*)/g;
  let lastIndex = 0;
  let match;

  while ((match = boldRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.substring(lastIndex, match.index));
    }

    const boldText = match[2] || match[4];
    parts.push(
      <strong key={`${keyPrefix}-${partIndex++}`} className="font-semibold">
        {boldText}
      </strong>
    );

    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex));
  }

  if (parts.length === 0) {
    parts.push(text);
  }

  return parts;
}

// Функция для парсинга markdown-подобного форматирования
function parseMarkdown(text: string): JSX.Element[] {
  const cleanText = text
    .replace(/```html\n?/gi, '')
    .replace(/```json\n?/gi, '')
    .replace(/```markdown\n?/gi, '')
    .replace(/```\n?/g, '')
    .replace(/^```$/gm, '')
    .trim();

  const lines = cleanText.split('\n');
  const elements: JSX.Element[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i] ?? '';

    if (line.startsWith('### ')) {
      elements.push(
        <h4 key={i} className="font-manrope font-semibold text-[14px] leading-[20px] text-[#0d0d0d] mt-[8px] mb-[4px]">
          {processBoldText(line.substring(4), `h4-${i}`)}
        </h4>
      );
      i++;
    } else if (line.startsWith('## ')) {
      elements.push(
        <h3 key={i} className="font-manrope font-semibold text-[20px] leading-[28px] text-[#0d0d0d] mt-[12px] mb-[8px]">
          {processBoldText(line.substring(3), `h3-${i}`)}
        </h3>
      );
      i++;
    } else if (line.startsWith('# ')) {
      elements.push(
        <h2 key={i} className="font-manrope font-bold text-[32px] leading-[40px] text-[#0d0d0d] mt-[16px] mb-[12px]">
          {processBoldText(line.substring(2), `h2-${i}`)}
        </h2>
      );
      i++;
    } else if (line.startsWith('* ')) {
      const listItems: JSX.Element[] = [];
      const startIndex = i;

      while (i < lines.length && (lines[i] ?? '').startsWith('* ')) {
        const itemText = (lines[i] ?? '').substring(2);
        listItems.push(
          <li key={i}>
            {processBoldText(itemText, `li-${i}`)}
          </li>
        );
        i++;
      }

      elements.push(
        <ul
          key={`ul-${startIndex}`}
          className="list-disc font-segoe text-[16px] leading-[24px] pl-[24px] space-y-[8px] my-[8px]"
        >
          {listItems}
        </ul>
      );
    } else if (line === '') {
      elements.push(<br key={i} />);
      i++;
    } else {
      const parts = processBoldText(line, `text-${i}`);

      if (parts.length > 0) {
        elements.push(
          <span key={i} className="block">
            {parts}
          </span>
        );
      }
      i++;
    }
  }

  return elements;
}

// Расширенный тип персоны с номерами для отображения
interface PersonaWithDisplay extends Persona {
  displayNumber?: number;
  totalForProject?: number;
}

const FREE_RESEARCH_LIMIT = 0;

export default function ResearchPage() {
  const { t } = useTranslation('research');
  const { lang } = useLanguage();
  const navigate = useNavigate();
  const { user, subscriptionTier } = useAuth();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Data state
  const [personas, setPersonas] = useState<PersonaWithDisplay[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedPersona, setSelectedPersona] = useState<PersonaWithDisplay | null>(null);

  const { setNoPadding } = useAppLayout();

  useLayoutEffect(() => {
    setNoPadding(!!selectedPersona);
    return () => setNoPadding(false);
  }, [selectedPersona, setNoPadding]);
  const [researchChats, setResearchChats] = useState<ResearchChat[]>([]);
  const [selectedChat, setSelectedChat] = useState<ResearchChat | null>(null);
  const [showChatHistory, setShowChatHistory] = useState(false);
  const [messages, setMessages] = useState<Array<{ role: 'user' | 'assistant'; content: string; timestamp: string }>>([]);

  // Chat state
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  // Permissions
  const isFree = subscriptionTier === 'free';
  const canCreateNewChat = !isFree || researchChats.filter(c => c.persona === selectedPersona?.id).length < FREE_RESEARCH_LIMIT;

  // Tooltip state
  const [tooltipVisible, setTooltipVisible] = useState<{ id: string; type: string } | null>(null);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    loadPersonas();
  }, [user, navigate]);

  useEffect(() => {
    if (selectedPersona) {
      loadResearchChats(selectedPersona.id);
    }
  }, [selectedPersona]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Load messages from chat_messages collection when selectedChat changes
  useEffect(() => {
    if (selectedChat) {
      loadMessages(selectedChat.id);
    } else {
      setMessages([]);
    }
  }, [selectedChat?.id]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const adjustTextareaHeight = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  };

  const loadMessages = async (chatId: string) => {
    try {
      const result = await chatMessagesApi.list(chatId, 'research', 1, 200);
      setMessages(result.messages.map(m => ({
        role: m.role,
        content: m.content,
        timestamp: m.created,
      })));
    } catch (error) {
      console.error('Error loading messages:', error);
      // Fallback to chat's JSON messages
      if (selectedChat) {
        setMessages(selectedChat.messages || []);
      }
    }
  };

  const loadPersonas = async () => {
    try {
      const [personasList, projectsList] = await Promise.all([
        personasApi.list(),
        projectsApi.list(),
      ]);
      setPersonas(personasList);
      setProjects(projectsList);
    } catch (error) {
      console.error('Error loading personas:', error);
    } finally {
      setIsInitialLoading(false);
    }
  };

  const loadResearchChats = async (personaId: string) => {
    try {
      const chatsList = await researchChatsApi.list();
      const personaChats = chatsList.filter(c => c.persona === personaId);
      setResearchChats(personaChats);

      // Auto-select the most recent chat if exists
      if (personaChats.length > 0 && personaChats[0]) {
        setSelectedChat(personaChats[0]);
      }
    } catch (error) {
      console.error('Error loading research chats:', error);
    }
  };

  const handleSelectPersona = (persona: PersonaWithDisplay) => {
    setSelectedPersona(persona);
    setSelectedChat(null);
    setResearchChats([]);
    setShowChatHistory(false);
  };

  const handleBackToPersonas = () => {
    setSelectedPersona(null);
    setSelectedChat(null);
    setResearchChats([]);
    setShowChatHistory(false);
  };

  const handleCreateNewChat = async () => {
    if (!canCreateNewChat || !selectedPersona) return;

    try {
      setIsLoading(true);

      const chat = await researchChatsApi.create(selectedPersona.project, selectedPersona.id);
      setSelectedChat(chat);
      setMessages([]);
      await loadResearchChats(selectedPersona.id);
      setShowChatHistory(false);
    } catch (error) {
      console.error('Error creating research chat:', error);
      toast.error(t('toast.chatCreateError'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!selectedChat || !inputMessage.trim() || !selectedPersona) return;

    const userMessage = inputMessage.trim();
    setInputMessage('');

    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
    setIsLoading(true);

    // Optimistic update: add user message immediately
    setMessages(prev => [...prev, { role: 'user' as const, content: userMessage, timestamp: new Date().toISOString() }]);

    try {
      const response = await aiApi.researchChat(
        selectedPersona.project,
        selectedPersona.id,
        userMessage,
        selectedChat.id,
        lang
      );

      if (response.chat) {
        setSelectedChat(response.chat);
        // Optimistic update: add assistant response immediately
        const chatMessages = response.chat.messages || [];
        const lastMsg = chatMessages[chatMessages.length - 1];
        if (lastMsg && lastMsg.role === 'assistant') {
          setMessages(prev => [...prev, { role: 'assistant' as const, content: lastMsg.content, timestamp: lastMsg.timestamp || new Date().toISOString() }]);
        }

        // Обновляем заголовок чата если это первое сообщение
        if (chatMessages.length === 2 && !response.chat.title) {
          const title = userMessage.split(/\s+/).slice(0, 3).join(' ');
          await researchChatsApi.updateTitle(response.chat.id, title);
        }

        await loadResearchChats(selectedPersona.id);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error(t('toast.messageError'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectChat = (chatId: string) => {
    const chat = researchChats.find(c => c.id === chatId);
    if (chat) {
      setSelectedChat(chat);
      setShowChatHistory(false);
    }
  };

  const canSendMessage = inputMessage.trim() && !isLoading;

  // Prepare chat history data for panel
  const chatHistoryData = researchChats.map(chat => ({
    id: chat.id,
    title: chat.title || t('chatHistory.chatDate', { date: new Date(chat.created).toLocaleDateString('ru-RU') }),
    created: chat.created,
  }));

  if (!isInitialLoading && isFree) {
    return (
      <><PageHeader title={t('title')} description={t('description')} />
        <SubscriptionLimitBanner
          title={t('paidBanner.title')}
          description={t('paidBanner.description')}
        />
      </>
    );
  }

  return (
    <>
      {!selectedPersona && (
        <PageHeader title={t('title')} description={t('description')} />
      )}
      {/* Chat History Panel */}
      <ChatHistoryPanel
        show={showChatHistory}
        chats={chatHistoryData}
        currentChatId={selectedChat?.id || null}
        onClose={() => setShowChatHistory(false)}
        onChatSelect={handleSelectChat}
        onNewChat={handleCreateNewChat}
        canCreateNewChat={canCreateNewChat}
      />

      {/* Persona Selection View */}
      {!selectedPersona && (
        <>

          {isInitialLoading ? (
            <div className="flex items-center justify-center py-[80px]">
              <div className="w-[32px] h-[32px] border-2 border-[#2197f1] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : personas.length === 0 ? (
            <div className="flex flex-col gap-[24px]">
              <div className="font-segoe text-[16px] leading-[24px] text-[#565858] max-w-[769px]">
                <p>
                  {t('empty.noPersonas')}
                </p>
              </div>
              <button
                onClick={() => navigate('/personas')}
                className="relative inline-block"
              >
                <div className="bg-[#2197f1] hover:bg-[#1a7bc7] rounded-[16px] px-[24px] py-[12px] transition-colors inline-block">
                  <p className="font-segoe font-semibold text-[16px] leading-[24px] text-white text-center">
                    Перейти к созданию персоны
                  </p>
                </div>
              </button>
            </div>
          ) : (
            <div className="flex flex-col lg:flex-row gap-[16px] md:gap-[24px]">
              {/* Left Column: Personas List */}
              <div className="flex flex-col gap-[8px] w-full lg:w-[424px]">
                {(() => {
                  const projectCounts: Record<string, number> = {};
                  const personaNumbers: Record<string, number> = {};

                  personas.forEach(persona => {
                    const projId = persona.project;
                    projectCounts[projId] = (projectCounts[projId] || 0) + 1;
                  });

                  personas.forEach(persona => {
                    const projId = persona.project;
                    if (!personaNumbers[projId]) {
                      personaNumbers[projId] = 0;
                    }
                    personaNumbers[projId]++;
                    persona.displayNumber = personaNumbers[projId];
                    persona.totalForProject = projectCounts[projId];
                  });

                  return personas.map((persona, index) => {
                    const isDisabled = isFree && index > 0;
                    const project = projects.find(p => p.id === persona.project);
                    const projectName = project?.name || 'Продукт';
                    const shouldShowNumber = (persona.totalForProject || 0) > 1;
                    const personaName = extractPersonaName(persona);
                    const displayTitle = shouldShowNumber
                      ? `${projectName} - ${personaName} ${persona.displayNumber}`
                      : `${projectName} - ${personaName}`;

                    return (
                      <button
                        key={persona.id}
                        onClick={() => !isDisabled && handleSelectPersona(persona)}
                        onMouseEnter={() => isDisabled && setTooltipVisible({ id: persona.id, type: 'select' })}
                        onMouseLeave={() => setTooltipVisible(null)}
                        disabled={isDisabled}
                        className="relative"
                      >
                        <div className={`${isDisabled
                          ? 'bg-[#f4f4f3] cursor-not-allowed'
                          : 'bg-[#f4f4f3] hover:bg-[#eeeeee]'
                          } rounded-[16px] px-[24px] py-[16px] flex items-center justify-center transition-colors`}>
                          <p className={`font-segoe text-[16px] leading-[24px] text-center ${isDisabled ? 'text-[#cecdc8]' : 'text-[#0d0d0d]'
                            }`}>
                            {displayTitle}
                          </p>
                        </div>

                        {tooltipVisible?.id === persona.id && isDisabled && (
                          <div className="absolute top-1/2 left-[calc(100%+16px)] -translate-y-1/2 bg-[rgba(13,13,13,0.8)] px-[24px] py-[12px] rounded-[16px] whitespace-nowrap z-10">
                            <p className="font-segoe text-[14px] leading-[20px] text-white text-center">
                              Доступно на платном тарифе
                            </p>
                          </div>
                        )}
                      </button>
                    );
                  });
                })()}
              </div>

            </div>
          )}
        </>
      )}

      {/* Chat View with Selected Persona */}
      {selectedPersona && (
        <div className="flex flex-col h-screen">
          {/* Header - sticky top */}
          <div className="sticky top-0 z-10 px-8 py-[16px] md:py-[20px] border-b border-[#f4f4f3]">
            <div className="flex items-center gap-[12px] md:gap-[16px]">
              {/* Back Button */}
              <button
                onClick={handleBackToPersonas}
                className="w-[20px] h-[20px] md:w-[24px] md:h-[24px] flex-shrink-0 rotate-[270deg] -scale-y-100"
              >
                <svg width="100%" height="100%" viewBox="0 0 24 24" fill="none">
                  <path d="M19.92 8.95L13.4 15.47C12.63 16.24 11.37 16.24 10.6 15.47L4.08 8.95" stroke="#0d0d0d" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" strokeMiterlimit="10" />
                </svg>
              </button>

              {/* Title */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-[8px] flex-wrap">
                  <h1 className="font-manrope font-bold text-[20px] md:text-[24px] lg:text-[32px] leading-[28px] md:leading-[32px] lg:leading-[40px] text-[#0d0d0d]">
                    {t('title')}
                  </h1>
                  <span className="hidden md:inline font-manrope font-semibold text-[20px] leading-[28px] text-[#0d0d0d]">
                    /
                  </span>
                  <span className="hidden md:inline font-manrope font-semibold text-[16px] md:text-[18px] lg:text-[20px] leading-[24px] md:leading-[26px] lg:leading-[28px] text-[#0d0d0d] truncate">
                    {extractPersonaName(selectedPersona)}
                  </span>
                </div>
              </div>

              {/* Right Actions */}
              <div className="flex items-center gap-[8px] md:gap-[12px] lg:gap-[16px]">
                {/* Chat History Toggle */}
                <button
                  onClick={() => setShowChatHistory(!showChatHistory)}
                  className="flex items-center gap-[8px] px-[8px] md:px-[12px] lg:px-[16px] py-[8px] md:py-[12px] rounded-[8px] hover:bg-[#eeeeee] transition-colors"
                >
                  <div className="relative shrink-0 size-[20px] md:size-[24px]">
                    <div className="absolute left-1/2 size-[14px] top-1/2 -translate-x-1/2 -translate-y-1/2">
                      <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 15 15">
                        <path clipRule="evenodd" fillRule="evenodd" d="M12.4499 12.449C10.3106 14.5885 7.14279 15.0508 4.55042 13.8519C4.16772 13.6978 3.85397 13.5733 3.55569 13.5733C2.72486 13.5782 1.69073 14.3838 1.15326 13.847C0.615797 13.3094 1.422 12.2745 1.422 11.4386C1.422 11.1403 1.3024 10.8322 1.14834 10.4487C-0.051112 7.85677 0.411785 4.6879 2.5511 2.54905C5.28205 -0.182901 9.71897 -0.182901 12.4499 2.54835C15.1858 5.28453 15.1809 9.71771 12.4499 12.449Z" stroke="#565858" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </div>
                  </div>
                  <span className="hidden md:inline font-manrope font-semibold text-[14px] leading-[20px] text-[#565858]">
                    {t('chatHistory.title')}
                  </span>
                </button>

                {/* New Chat Button */}
                <button
                  onClick={() => canCreateNewChat && handleCreateNewChat()}
                  disabled={!canCreateNewChat}
                  onMouseEnter={() => !canCreateNewChat && setTooltipVisible({ id: 'new-chat', type: 'create' })}
                  onMouseLeave={() => setTooltipVisible(null)}
                  className="relative flex items-center gap-[8px] px-[16px] py-[12px] rounded-[8px] hover:bg-[#eeeeee] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <div className="overflow-clip relative shrink-0 size-[24px]">
                    <div className="absolute left-[4px] size-[16px] top-[4px]">
                      <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 17 17">
                        <path d="M0.5 8.5H16.5M8.5 16.5V8.5L8.5 0.5" stroke="#565858" strokeLinecap="round" />
                      </svg>
                    </div>
                  </div>
                  <span className="font-manrope font-semibold text-[14px] leading-[20px] text-[#565858]">
                    {t('chatHistory.newChat')}
                  </span>

                  {tooltipVisible?.id === 'new-chat' && !canCreateNewChat && (
                    <div className="absolute top-[calc(100%+8px)] left-1/2 -translate-x-1/2 bg-[rgba(13,13,13,0.8)] px-[24px] py-[12px] rounded-[16px] whitespace-nowrap z-10">
                      <p className="font-segoe text-[14px] leading-[20px] text-white text-center">
                        {t('tooltip.newChatLimit')}
                      </p>
                    </div>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Chat Messages Area */}
          <div className="flex-1 overflow-y-auto py-[24px] px-8">
            {!selectedChat ? (
              <div className="flex flex-col items-center justify-center h-full gap-[24px]">
                <p className="font-segoe text-[16px] leading-[24px] text-[#565858] text-center max-w-[500px]">
                  {t('chat.createPrompt', { personaName: extractPersonaName(selectedPersona) })}
                </p>
                <button
                  onClick={handleCreateNewChat}
                  disabled={!canCreateNewChat}
                  className={`${canCreateNewChat
                    ? 'bg-[#2197f1] hover:bg-[#1a7bc7]'
                    : 'bg-[rgba(33,151,241,0.2)] cursor-not-allowed'
                    } rounded-[16px] px-[24px] py-[12px] transition-colors`}
                >
                  <p className="font-segoe font-semibold text-[16px] leading-[24px] text-white text-center">
                    Создать чат
                  </p>
                </button>
              </div>
            ) : (
              <div className="max-w-[600px] mx-auto">
                {messages.length === 0 ? (
                  <div className="flex flex-col gap-[16px] mb-[24px]">
                    <p className="font-segoe text-[16px] leading-[24px] text-[#0d0d0d]">
                      {t('chat.greeting', { personaName: extractPersonaName(selectedPersona) })}
                    </p>
                  </div>
                ) : (
                  <div className="flex flex-col gap-[16px]">
                    {/* Initial Greeting */}
                    <div className="flex flex-col gap-[16px] mb-[8px]">
                      <p className="font-segoe text-[16px] leading-[24px] text-[#0d0d0d]">
                        {t('chat.greeting', { personaName: extractPersonaName(selectedPersona) })}
                      </p>
                    </div>

                    {messages.map((message, idx) => (
                      <div
                        key={idx}
                        className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                      >
                        <div className={`${message.role === 'user'
                          ? 'bg-white rounded-[24px] px-[16px] py-[8px]'
                          : ''
                          } max-w-[600px]`}>
                          <div className="font-segoe text-[16px] leading-[24px] text-[#0d0d0d]">
                            {message.role === 'assistant' ? (
                              <>{parseMarkdown(message.content)}</>
                            ) : (
                              <p>{message.content}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}

                    {isLoading && (
                      <div className="flex justify-start">
                        <div>
                          <div className="flex gap-[8px] items-center">
                            <div className="w-[8px] h-[8px] bg-[#565858] rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                            <div className="w-[8px] h-[8px] bg-[#565858] rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                            <div className="w-[8px] h-[8px] bg-[#565858] rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                          </div>
                        </div>
                      </div>
                    )}

                    <div ref={messagesEndRef} />
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Input Area - sticky bottom */}
          {selectedChat && (
            <div className="sticky bottom-0 z-10 py-[16px] px-8 border-t border-[#f4f4f3]">
              <div className="max-w-[600px] mx-auto">
                {/* Input Box */}
                <div className="bg-white rounded-[24px] p-[8px] flex items-center gap-[8px] border border-[#eeeeee]">
                  {/* Text Input */}
                  <textarea
                    ref={textareaRef}
                    value={inputMessage}
                    onChange={(e) => {
                      setInputMessage(e.target.value);
                      adjustTextareaHeight();
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey && canSendMessage) {
                        e.preventDefault();
                        handleSendMessage();
                      }
                    }}
                    placeholder={t('chat.inputPlaceholder')}
                    disabled={isLoading}
                    rows={1}
                    className="flex-1 bg-transparent font-segoe text-[16px] leading-[24px] text-[#0d0d0d] placeholder:text-[#969696] outline-none resize-none overflow-hidden px-[8px]"
                  />

                  {/* Send Button */}
                  <button
                    onClick={handleSendMessage}
                    disabled={!canSendMessage}
                    className={`p-[8px] rounded-[16px] transition-colors flex-shrink-0 ${canSendMessage
                      ? 'bg-[#2197f1] hover:bg-[#1a7bc7]'
                      : 'bg-[rgba(33,151,241,0.2)] cursor-not-allowed'
                      }`}
                  >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path d="M7.4 6.32L15.89 3.49C19.7 2.22 21.77 4.3 20.51 8.11L17.68 16.6C15.78 22.31 12.66 22.31 10.76 16.6L9.92 14.08L7.4 13.24C1.69 11.34 1.69 8.23 7.4 6.32Z" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M10.1101 13.6496L13.6901 10.0596" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                </div>

                {/* Disclaimer */}
                <p className="font-segoe text-[10px] leading-[16px] text-[#565858] text-center mt-[8px]">
                  {t('chat.disclaimer')}
                </p>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
}
